import { Command, LayoutDashboard, Newspaper, Share2 } from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Command Center is leadership-only — mirrors the nav gate. */
const CC_ROLES: Role[] = ['super_admin', 'founder'];

export const COMMAND_CENTER_GUIDES: Guide[] = [
  {
    slug: 'cc-overview',
    title: 'The leadership cockpit',
    module: 'command-center',
    summary: 'A cross-module snapshot plus outside signals, for leadership.',
    icon: Command,
    roles: CC_ROLES,
    body: `**Command Center** is the leadership view — the whole business on one screen,
plus signals from outside it. It's a **leadership-only** view.

### What's here
- **Executive Dashboard** — the live cross-module snapshot (sales, delivery, money).
- **Channel Analytics** — where leads are coming from (Upwork, LinkedIn, Website).
- **Market Trends & News Feed** — industry signals from your configured sources.

### Why it's here
Leadership shouldn't have to open six modules to take the pulse of the business.
Command Center pulls the headline numbers and external context into one place to
support fast, informed decisions.`,
  },
  {
    slug: 'cc-executive-dashboard',
    title: 'Executive Dashboard',
    module: 'command-center',
    summary: 'Open deals, active projects, hours, runway, money and lead inflow.',
    icon: LayoutDashboard,
    roles: CC_ROLES,
    body: `The **Executive Dashboard** is a one-screen, cross-module snapshot of the
active organization. All money figures are in **base USD**.

### What it shows
- **Open deals** — your live CRM pipeline count.
- **Active projects** — what delivery has in flight.
- **Tracked hours** — logged time across projects.
- **Runway** — net ÷ average monthly burn (months of cushion).
- **Income / Expenses / Net / Outstanding AR** — the headline finance numbers.
- **Lead inflow by channel** — where new leads came from (Website, Upwork, LinkedIn,
  Referral, Outbound…).

### Why it's here
One honest pulse-check that spans sales, delivery and finance — so leadership can
see how the business is doing without stitching together separate reports.`,
  },
  {
    slug: 'cc-channel-analytics',
    title: 'Channel Analytics',
    module: 'command-center',
    summary: 'Lead inflow by source — Upwork, LinkedIn and Website.',
    icon: Share2,
    roles: CC_ROLES,
    body: `**Channel Analytics** breaks down where your leads come from, one view per
source: **Upwork**, **LinkedIn** and **Website**.

### What it shows today
- **Lead inflow** for each channel, drawn from your CRM leads' **source**.
- This works out of the box — no integration required to see the inflow split.

### What deepens with an integration
Richer, source-native analytics (profile stats, site traffic, etc.) appear once
the relevant **integration is connected**. The Website view surfaces the CRM
"website" channel until a web-analytics provider (e.g. GA / Plausible) is wired up.

### Why it's here
Knowing which channels actually produce leads tells you where to invest effort and
spend — grounded in your own pipeline data, not guesswork.`,
  },
  {
    slug: 'cc-feeds',
    title: 'Market Trends & News',
    module: 'command-center',
    summary: 'Outside signals from your configured RSS / news / market sources.',
    icon: Newspaper,
    roles: CC_ROLES,
    body: `**Market Trends** and the **News Feed** bring the outside world in — curated
from the **RSS / news / market sources** you configure.

### How to use it
- **News Feed** — industry news from your news/RSS sources.
- **Market Trends** — aggregated trend data from your market sources.
- Both populate as sources are configured; with none set up, they'll be empty.

### Why it's here
Good decisions need context beyond your own numbers. Pulling relevant news and
market signals into the cockpit keeps leadership aware of what's happening around
the business — without leaving the app.`,
  },
];
